import { useEffect, useMemo, useRef, useState } from "react";
import { useBlocker, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { axiosInterceptor } from "app/services/http-services/request-interceptor";
import { RecordManagement } from "app/class/record-management";
import { useTables } from "app/hooks/useTables";
import { useColumns } from "app/hooks/useColumns";
import { resolveEmbeddedKeys } from "modules/record/shared/services/recordMetadataLabels";
import { FormLayoutData } from "modules/record/record-producer/services/formLayoutBuilder";
import { addField, EditorSection, emptySection, moveField, nextSectionName, removeField, suggestViewName, toEditorSections, toSavePayload } from "./layoutState";
import BuilderToolbar from "./components/BuilderToolbar";
import FieldPalette from "./components/FieldPalette";
import NewViewDialog from "./components/NewViewDialog";
import PreviewPanel from "./components/PreviewPanel";
import SectionCard from "./components/SectionCard";
import ViewList from "./components/ViewList";
import TranslatableInput from "component/global/translation/TranslatableInput";
import { RoleLink, RoleRecord, ViewRecord } from "./components/types";

const uid = () => globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
const viewsApi = new RecordManagement<ViewRecord>("sys_form_views");
const roleLinksApi = new RecordManagement<RoleLink>("sys_form_view_roles");
const visuallyHidden = { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" } as const;
type LayoutResponse = FormLayoutData & { updated_at: string };

export default function FormBuilderPage() {
    const { tableName, viewId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const tables = useTables();
    const allColumns = useColumns();
    const table = tables.data.find(item => item.name === tableName);
    const allPage = !tableName;
    const tableOptions = useMemo(() => tables.data.map(item => ({ id: item.id, name: item.name, label: String(t(item.label)) })), [tables.data, i18n.language]);
    const filterTable = useMemo(() => tableOptions.find(item => item.name === searchParams.get("table")) ?? null, [tableOptions, searchParams]);
    const parent = tables.data.find(item => item.name === table?.parent_table_name);
    const columns = useMemo(() => allColumns.data.filter(item => item.sys_table_id === table?.id && item.active && !item.is_hidden), [allColumns.data, table?.id]);
    const [views, setViews] = useState<ViewRecord[]>([]);
    const [roles, setRoles] = useState<RoleRecord[]>([]);
    const [links, setLinks] = useState<RoleLink[]>([]);
    const [sections, setSections] = useState<EditorSection[]>([]);
    const [stamp, setStamp] = useState("");
    const [viewLabel, setViewLabel] = useState("");
    const viewLabelRef = useRef("");
    const [dirty, setDirty] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [busyAction, setBusyAction] = useState("");
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [search, setSearch] = useState("");
    const [sampleSearch, setSampleSearch] = useState("");
    const [samples, setSamples] = useState<{ id: string; number_ref?: string }[]>([]);
    const [sampleId, setSampleId] = useState("");
    const [previewOpen, setPreviewOpen] = useState(true);
    const [newViewOpen, setNewViewOpen] = useState(false);
    const [confirm, setConfirm] = useState<{ title: string; action: () => void } | null>(null);
    const [dragId, setDragId] = useState("");
    const [announcement, setAnnouncement] = useState("");
    const focusRef = useRef<string | null>(null);
    const translationFlushers = useRef(new Map<string, () => Promise<boolean>>());
    const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty &&
        (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search));

    const tr = (key: string, fallback: string, options?: Record<string, unknown>) => String(t(`form_layout.builder.${key}`, { defaultValue: fallback, ...options }));
    const fillTable = (value: string, contextTableLabel = tableLabel) => value.replace(/{{\s*table\s*}}/g, contextTableLabel);
    const text = (label: string, view?: ViewRecord) => {
        const contextTableLabel = view ? tableOptions.find(item => item.id === view.sys_table_id)?.label ?? tableLabel : tableLabel;
        return fillTable(resolveEmbeddedKeys(t, String(t(label, { defaultValue: label, table: contextTableLabel })), { table: contextTableLabel }), contextTableLabel);
    };
    const sectionLabel = (section: EditorSection) => section.display !== undefined ? fillTable(section.display) : text(section.label);
    const columnLabel = (column: { label: string }) => String(t(column.label));
    const tableLabel = table ? String(t(table.label)) : "";
    const parentLabel = String(t(parent?.label ?? parent?.name ?? ""));
    const fail = (reason: any) => {
        const raw = reason?.response?.data?.error?.message || reason?.response?.data?.error;
        setError(typeof raw === "string" ? String(t(raw, { defaultValue: raw })) : tr("action_failed", "Operation failed. Please try again."));
    };

    const load = async () => {
        if (tableName && !table) return;
        setLoading(true);
        try {
            const [own, inherited, roleRows, linkRows] = await Promise.all([
                (table ? new RecordManagement<ViewRecord>("sys_form_views").where("sys_table_id", table.id) : new RecordManagement<ViewRecord>("sys_form_views")).paginate(1, 5000).run(),
                parent ? new RecordManagement<ViewRecord>("sys_form_views").where("sys_table_id", parent.id).paginate(1, 5000).run() : Promise.resolve({ data: [] as ViewRecord[] }),
                new RecordManagement<RoleRecord>("sys_roles").paginate(1, 5000).run(),
                new RecordManagement<RoleLink>("sys_form_view_roles").paginate(1, 5000).run(),
            ]);
            setViews([...(own.data ?? []), ...(inherited.data ?? []).filter(item => item.active).map(item => ({ ...item, inherited_from_table: parent?.name }))]);
            setRoles(roleRows.data ?? []);
            setLinks(linkRows.data ?? []);
            setError("");
        } catch (reason: any) { fail(reason); }
        finally { setLoading(false); }
    };
    useEffect(() => { void load(); }, [tableName, table?.id, parent?.id]);
    useEffect(() => {
        const chosen = views.find(item => item.id === viewId);
        if (chosen && !dirty) { setViewLabel(chosen.label); viewLabelRef.current = chosen.label; }
        if (viewId && chosen && chosen.sys_table_id !== table?.id) navigate(`/home/form-builder/${tableName}`, { replace: true });
    }, [views, viewId, table?.id, tableName, navigate]);

    // Sair do editor (descartar ou voltar) encerra o estado sujo; senão o próximo Editar pergunta de novo.
    useEffect(() => { if (!viewId) { setDirty(false); setSections([]); setMessage(""); } }, [viewId]);
    useEffect(() => {
        if (!viewId || !table) return;
        let active = true;
        setLoading(true);
        axiosInterceptor.get<{ data: LayoutResponse }>(`/api/form_layout/views/${viewId}/layout`, { params: { table_id: table.id, language: i18n.language } })
            .then(response => {
                if (!active) return;
                const data = response.data.data;
                setSections(toEditorSections(data as any));
                setStamp(data.updated_at);
                setDirty(false);
            }).catch(fail)
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [viewId, table?.id, i18n.language]);

    useEffect(() => {
        if (!dirty) return;
        const handler = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [dirty]);
    useEffect(() => {
        if (!focusRef.current) return;
        document.querySelector<HTMLElement>(`[data-field-id="${focusRef.current}"]`)?.focus();
        focusRef.current = null;
    }, [sections]);

    const mutateSections = (next: EditorSection[], announcementText?: string, fieldId?: string) => {
        setSections(next); setDirty(true); setMessage("");
        if (announcementText) setAnnouncement(announcementText);
        if (fieldId) focusRef.current = fieldId;
    };
    const updateSection = (id: string, patch: Partial<EditorSection>) => mutateSections(sections.map(item => item.id === id ? { ...item, ...patch } : item));
    const fieldName = (id: string) => { const column = columns.find(item => item.id === id); return column ? columnLabel(column) : id; };
    const move = (fieldId: string, sectionId: string, col: "1" | "2", position: number) => mutateSections(
        moveField(sections, fieldId, sectionId, col, position), `${tr("moved", "Field moved")}: ${fieldName(fieldId)}`, fieldId);
    const add = (fieldId: string) => {
        if (!sections[0]) { setAnnouncement(tr("no_sections", "Add a section to start arranging fields")); return; }
        mutateSections(addField(sections, sections[0].id, "1", fieldId), `${tr("added", "Field added")}: ${fieldName(fieldId)}`, fieldId);
    };
    const remove = (fieldId: string) => mutateSections(removeField(sections, fieldId),
        tr("returned_to_palette", "{{field}} returned to the field list", { field: fieldName(fieldId) }));
    const moveSection = (index: number, offset: number) => {
        const next = [...sections]; [next[index], next[index + offset]] = [next[index + offset], next[index]];
        mutateSections(next, tr("section_moved", "Section moved"));
    };

    const create = async (name: string, chosenTable?: { id: string; name: string }) => {
        const target = table ?? chosenTable ?? filterTable;
        if (!target || busyAction) return;
        setBusyAction("create");
        try {
            const view = await viewsApi.save({ sys_table_id: target.id, label: name, active: true, is_default: false });
            setNewViewOpen(false);
            navigate(`/home/form-builder/${target.name}/${view.id}`);
            await load();
        } catch (reason: any) { fail(reason); setNewViewOpen(false); }
        finally { setBusyAction(""); }
    };
    const duplicate = async (source: ViewRecord) => {
        if (busyAction) return;
        setBusyAction(source.id);
        try {
            await axiosInterceptor.post(`/api/form_layout/views/${source.id}/duplicate`, { table_id: source.sys_table_id, language: i18n.language }, { timeout: 60000 });
            await load();
        } catch (reason: any) {
            if (!reason?.response) { await load(); setError(tr("action_unconfirmed", "The operation may have completed. The list was refreshed; check before trying again.")); }
            else fail(reason);
        } finally { setBusyAction(""); }
    };
    const extend = async (source: ViewRecord) => {
        if (!table || busyAction) return;
        setBusyAction(source.id);
        try {
            await axiosInterceptor.post(`/api/form_layout/views/${source.id}/extend`, { table_id: table.id, language: i18n.language }, { timeout: 60000 });
            await load();
        } catch (reason: any) {
            if (!reason?.response) { await load(); setError(tr("action_unconfirmed", "The operation may have completed. The list was refreshed; check before trying again.")); }
            else fail(reason);
        } finally { setBusyAction(""); }
    };
    const setDefault = async (target: ViewRecord) => {
        if (busyAction) return;
        setBusyAction(target.id);
        try {
            const previous = views.find(view => view.sys_table_id === target.sys_table_id && view.is_default);
            if (previous && previous.id !== target.id) await viewsApi.update(previous.id, { is_default: false });
            await viewsApi.update(target.id, { is_default: true });
            await load();
        } catch (reason: any) { fail(reason); }
        finally { setBusyAction(""); }
    };
    const toggleActive = async (view: ViewRecord) => {
        if (busyAction) return;
        setBusyAction(view.id);
        try { await viewsApi.update(view.id, { active: !view.active }); await load(); }
        catch (reason: any) { fail(reason); }
        finally { setBusyAction(""); }
    };
    const deleteView = (view: ViewRecord) => setConfirm({
        title: tr("delete_view_confirm", "Delete the view {{view}}? Its sections, fields and roles will also be deleted.", { view: text(view.label, view) }),
        action: () => { void (async () => {
            if (busyAction) return;
            setBusyAction(view.id);
            try { await viewsApi.delete(view.id); await load(); }
            catch (reason: any) { fail(reason); }
            finally { setBusyAction(""); }
        })(); },
    });
    const changeRoles = async (view: ViewRecord, nextIds: string[]) => {
        const current = links.filter(link => link.sys_form_view_id === view.id);
        try {
            for (const link of current.filter(link => !nextIds.includes(link.sys_role_id))) await roleLinksApi.delete(link.id);
            for (const roleId of nextIds.filter(id => !current.some(link => link.sys_role_id === id))) await roleLinksApi.save({ sys_form_view_id: view.id, sys_role_id: roleId });
            await load();
        } catch (reason: any) { fail(reason); }
    };
    const save = async () => {
        if (!viewId) return;
        setSaving(true); setError("");
        let sentStamp = stamp;
        let layoutRequested = false;
        try {
            for (const flush of translationFlushers.current.values()) {
                if (!await flush()) { setSaving(false); return; }
            }
            if (viewLabelRef.current !== selected?.label) {
                const updated = await viewsApi.update(viewId, { label: viewLabelRef.current });
                sentStamp = updated.updated_at;
                setStamp(sentStamp);
                setViews(current => current.map(item => item.id === viewId ? updated : item));
            }
            layoutRequested = true;
            const response = await axiosInterceptor.put<{ data: LayoutResponse }>(`/api/form_layout/views/${viewId}/layout`, {
                updated_at: sentStamp, language: i18n.language, sections: toSavePayload(sections),
            }, { timeout: 60000 });
            const saved = response.data.data;
            setSections(toEditorSections(saved as any));
            setStamp(saved.updated_at); setDirty(false); setMessage(tr("saved", "Layout saved"));
        } catch (reason: any) {
            if (reason?.response?.status === 409) setError(tr("conflict", "Another person saved this view. Reload before saving."));
            else if (!reason?.response && layoutRequested) {
                try {
                    const current = (await axiosInterceptor.get<{ data: LayoutResponse }>(`/api/form_layout/views/${viewId}/layout`, { params: { table_id: table?.id, language: i18n.language } })).data.data;
                    if (current.updated_at !== sentStamp) {
                        setSections(toEditorSections(current as any)); setStamp(current.updated_at); setDirty(false); setMessage(tr("saved", "Layout saved")); setError("");
                    } else setError(tr("save_unconfirmed", "Could not confirm the save. Reload before trying again."));
                } catch { setError(tr("save_unconfirmed", "Could not confirm the save. Reload before trying again.")); }
            } else fail(reason);
        }
        finally { setSaving(false); }
    };
    const previewLayout: FormLayoutData = useMemo(() => ({
        sections: sections.map(section => ({ id: section.id, label: section.display ?? section.label, order: section.order, active: section.active,
            layout_columns: section.layout_columns, render_mode: section.render_mode, show_label: section.show_label !== false, sys_form_view_id: viewId })),
        elements: sections.flatMap(section => section.elements.map(element => ({ ...element, sys_form_section_id: section.id }))),
    }), [sections, viewId]);
    const used = new Set(sections.flatMap(section => section.elements.map(element => element.sys_column_id)));
    const mandatoryOutside = columns.filter(column => column.is_mandatory && column.name !== "sys_class_name" && !used.has(column.id));
    const selected = views.find(view => view.id === viewId);
    const searchSamples = async (value: string) => {
        setSampleSearch(value);
        if (!table) return;
        try { const result = await new RecordManagement<{ id: string; number_ref?: string }>(table.name).paginate(1, 20).run(); setSamples(result.data ?? []); } catch { setSamples([]); }
    };

    if (tables.isLoading) return <CircularProgress />;
    if (tableName && !table) return <Alert severity="error">{tr("table_missing", "Table not found")}</Alert>;
    const editing = Boolean(viewId);
    const visibleViews = allPage && filterTable ? views.filter(view => view.sys_table_id === filterTable.id) : views;
    return <Box sx={{ position: "relative", boxSizing: "border-box", p: { xs: 2, md: 3 }, width: "100%", minWidth: 0, maxWidth: "100%", overflowX: "hidden" }}>
        <BuilderToolbar title={allPage ? tr("all_views_title", "Form View Builder") : `${tr("title", "Form builder")} · ${tableLabel}`} editing={editing} dirty={dirty} saving={saving} busy={Boolean(busyAction)} previewOpen={previewOpen}
            onBack={() => navigate(`/home/form-builder/${table?.name}`)} onSave={() => void save()} onNew={() => setNewViewOpen(true)} onTogglePreview={() => setPreviewOpen(open => !open)} tr={tr} />
        <Stack spacing={2} sx={{ minWidth: 0 }}>
            {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
            {allPage && <Autocomplete sx={{ maxWidth: 440 }} options={tableOptions} value={filterTable}
                getOptionLabel={option => `${option.label} · ${option.name}`} isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options, state) => { const query = state.inputValue.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase(); return options.filter(option => `${option.label} ${option.name}`.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes(query)); }}
                onChange={(_, value) => setSearchParams(value ? { table: value.name } : {})}
                renderInput={params => <TextField {...params} label={tr("filter_table", "Filter by table")} />} />}
            {loading ? <CircularProgress /> : !editing ? <ViewList views={visibleViews} roles={roles} links={links} parentLabel={parentLabel} labelFor={text} tr={tr}
                showTable={allPage} busyAction={busyAction} tableLabelFor={view => tableOptions.find(item => item.id === view.sys_table_id) ?? { label: view.sys_table_id, name: view.sys_table_id }}
                actions={{ onEdit: view => navigate(`/home/form-builder/${tableOptions.find(item => item.id === view.sys_table_id)?.name ?? table?.name}/${view.id}`), onDuplicate: view => void duplicate(view), onExtend: view => void extend(view),
                    onSetDefault: view => void setDefault(view), onToggleActive: view => void toggleActive(view), onDelete: deleteView,
                    onRolesChange: (view, ids) => void changeRoles(view, ids), onCreate: () => setNewViewOpen(true) }} /> : <>
                <Typography variant="h6" component="h2">{selected ? text(selected.label) : tr("editor", "Edit view")}{dirty ? " *" : ""}</Typography>
                {selected && <Box sx={{ maxWidth: 440 }}><TranslatableInput label={tr("view_name", "View name")} value={viewLabel}
                    suggestedKey={`record.sys_form_views.label.${viewId}`} fallbackText={text(viewLabel)} defaultPublic={false}
                    registerFlush={flush => { translationFlushers.current.set("view", flush); return () => { translationFlushers.current.delete("view"); }; }}
                    onChange={value => { viewLabelRef.current = value; setViewLabel(value); setDirty(true); setMessage(""); }} /></Box>}
                {mandatoryOutside.length > 0 && <Alert severity="warning">{tr("mandatory_outside", "Required fields outside the form appear in Other fields")}: {mandatoryOutside.map(columnLabel).join(", ")}</Alert>}
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: previewOpen ? "minmax(220px, 260px) minmax(0, 1fr) minmax(300px, 32%)" : "minmax(220px, 260px) minmax(0, 1fr)" }, gap: 2, alignItems: "start", minWidth: 0 }}>
                    <FieldPalette columns={columns} ownTableId={table!.id} parentLabel={parentLabel} used={used} search={search} onSearch={setSearch} onAdd={add} onDrag={setDragId} labelFor={columnLabel} tr={tr} />
                    <Stack spacing={2} sx={{ minWidth: 0 }}>
                        {sections.length === 0 && <Alert severity="info">{tr("no_sections", "Add a section to start arranging fields")}</Alert>}
                        {sections.map((section, index) => <SectionCard key={section.id} section={section} sections={sections} index={index} columns={columns} ownTableId={table!.id} parentLabel={parentLabel}
                            sectionLabel={sectionLabel} labelFor={columnLabel} dragId={dragId} tr={tr}
                            onLabelChange={(value, display) => updateSection(section.id, { label: value, display })}
                            registerFlush={flush => { translationFlushers.current.set(section.id, flush); return () => { translationFlushers.current.delete(section.id); }; }}
                            onColumnsChange={count => mutateSections(sections.map(item => item.id === section.id ? { ...item, layout_columns: count, elements: item.elements.map(element => ({ ...element, column_index: count === "1" ? "1" as const : element.column_index })) } : item))}
                            onRenderModeChange={mode => updateSection(section.id, { render_mode: mode })}
                            onShowLabelChange={show => updateSection(section.id, { show_label: show })}
                            onSectionMove={offset => moveSection(index, offset)}
                            onDeleteSection={() => setConfirm({ title: tr("delete_section_confirm", "Delete this section?"), action: () => mutateSections(sections.filter(item => item.id !== section.id), tr("section_deleted", "Section deleted")) })}
                            onMoveField={(id, sectionId, col, position) => { move(id, sectionId, col, position); setDragId(""); }} onRemoveField={remove} onDrag={setDragId} />)}
                        <Box><Button variant="outlined" onClick={() => mutateSections([...sections, emptySection(`new:${uid()}`, nextSectionName(sections.map(sectionLabel), n => tr("section_n", "Section {{n}}", { n })))])}>{tr("add_section", "Add section")}</Button></Box>
                    </Stack>
                    {previewOpen && <PreviewPanel tableName={table!.name} viewId={viewId} layout={previewLayout} search={sampleSearch} samples={samples} sampleId={sampleId} onSearch={value => void searchSamples(value)} onSample={setSampleId} tr={tr} />}
                </Box>
            </>}
        </Stack>
        <Snackbar open={Boolean(message)} autoHideDuration={4000} onClose={() => setMessage("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
            <Alert severity="success" variant="filled" onClose={() => setMessage("")}>{message}</Alert>
        </Snackbar>
        <Box role="status" aria-live="polite" sx={visuallyHidden}>{announcement}</Box>
        <NewViewDialog open={newViewOpen} suggestion={suggestViewName(tableLabel || filterTable?.label || "View", views.filter(view => !view.inherited_from_table).map(view => text(view.label)))}
            tables={allPage ? tableOptions : undefined} selectedTable={allPage ? filterTable : undefined} busy={Boolean(busyAction)}
            onCancel={() => setNewViewOpen(false)} onCreate={(name, chosen) => void create(name, chosen)} tr={tr} />
        <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)}><DialogTitle>{confirm?.title}</DialogTitle><DialogContent>{tr("confirm_action", "This action can change the current layout.")}</DialogContent><DialogActions><Button variant="text" color="inherit" onClick={() => setConfirm(null)}>{tr("cancel", "Cancel")}</Button><Button variant="contained" onClick={() => { confirm?.action(); setConfirm(null); }} color="error">{tr("confirm", "Confirm")}</Button></DialogActions></Dialog>
        <Dialog open={blocker.state === "blocked"} onClose={() => blocker.reset?.()}><DialogTitle>{tr("discard", "Discard unsaved changes?")}</DialogTitle><DialogActions><Button variant="text" color="inherit" onClick={() => blocker.reset?.()}>{tr("cancel", "Cancel")}</Button><Button variant="contained" color="error" onClick={() => { setDirty(false); blocker.proceed?.(); }}>{tr("confirm", "Confirm")}</Button></DialogActions></Dialog>
    </Box>;
}
